import { useEffect, useRef, useState } from 'react';

import { useTheme } from '../features/config';
import { resolveCSSVariable } from '../utils/resolveCSSVariable';

export type UseThemeTokenReturnType<T> = T extends string
    ? string
    : T extends string[]
      ? string[]
      : never;

/**
 * Hook to resolve CSS variables from the theme.
 *
 * This hook will return the resolved CSS variable value(s) based on the current theme.
 *
 * The hook will also update the resolved value(s) when the theme changes.
 *
 * @usage
 * ### Single CSS variable
 *
 * ```jsx
 * import { useThemeToken } from 'styled-system';
 *
 * const colorRed = useThemeToken('--core-color-red-500');
 * ```
 *
 * ### Multiple CSS variables
 *
 * ```jsx
 * import { useThemeToken } from 'styled-system';
 *
 * const [colorRed, colorBlue] = useThemeToken([
 *     '--core-color-red-500',
 *     '--core-color-blue-500'
 * ]);
 * ```
 * @param cssVar - CSS variable name or array of CSS variable names.
 * @param rootElement - Root element to resolve CSS variables from.
 */
export const useThemeToken = <T,>(
    cssVar: T,
    rootElement?: HTMLElement
): UseThemeTokenReturnType<T> => {
    const { mode } = useTheme();
    const cssVarArray = Array.isArray(cssVar) ? cssVar : [cssVar];
    const computedRoot = rootElement || document.documentElement;
    const [variables, setVariables] = useState<string[]>([]);

    const timeoutId = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        timeoutId.current = setTimeout(() => {
            const computed = cssVarArray.map((variable) =>
                resolveCSSVariable(variable, computedRoot)
            );
            setVariables(computed);
        }, 50);

        return () => {
            if (timeoutId.current) {
                clearTimeout(timeoutId.current);
            }
        };
    }, [mode, cssVarArray.join(','), computedRoot]);

    return (typeof cssVar === 'string' ? variables[0] : variables) as UseThemeTokenReturnType<T>;
};
